/**
 * `/inventory/inbound` — blanks on their way from S&S (inbound-visibility Phases 1 + 1.5; relevance
 * split + dismiss = inventory-clarity P1). The receiving-side complement to the purchase draft. Pulled
 * live from the S&S ORDER FEED (not FSP purchase orders, so it reflects what's physically moving
 * regardless of how it was ordered):
 *  - **Delivered — needs receiving**: boxes the carrier delivered whose S&S invoice has no RECEIVE
 *    movement yet AND that carry blanks FSP actually stocks. The receiving to-do — the only section
 *    with urgency framing, and the only one the hub badge counts.
 *  - **Not tracked by FSP**: delivered + unreceived, but no line has blank-ledger history — an account
 *    order for goods FSP doesn't stock. Neutral, collapsed; receiving it would create dead inventory.
 *  - **In transit**: shipped, not yet delivered — soonest-ETA first, with S&S's ETA + carrier tracking.
 *  - **Dismissed**: shipments the operator dismissed (display-only marker, reversible here).
 * ADVISORY: this board places no order and writes no ledger; Dismiss writes only a display marker.
 * `degraded` = the S&S feed (or a ledger lookup) was briefly unreachable.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  PackageCheck,
  Truck,
  Undo2,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiClient } from '@/lib/api/hooks';
import type { components } from '@/lib/api/types.gen';
import { useAuth } from '@/lib/auth-state';
import { cn } from '@/lib/utils';
import { blankLabel, variantLabel } from './inventory-lib';

type Shipment = components['schemas']['InventoryInboundShipment'];
type Mode = 'transit' | 'awaiting' | 'notTracked' | 'dismissed';

/** react-query key for the inbound board — shared with the hub (its badge reads the same query). */
const INBOUND_KEY = ['inventory-inbound'] as const;

/** Parse S&S's no-timezone `YYYY-MM-DDTHH:mm:ss` into a LOCAL date from its parts, so it never shifts a
 *  day across timezones (a bare `new Date('2026-07-21')` would be UTC midnight → the prior day in the US). */
function ymd(iso: string | null): Date | null {
  if (!iso) return null;
  const parts = iso.slice(0, 10).split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
  const [y, m, d] = parts as [number, number, number];
  return new Date(y, m - 1, d);
}

function dateLabel(d: Date): string {
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function daysFromToday(d: Date): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86_400_000);
}

function formatEta(iso: string | null): {
  label: string;
  relative: string | null;
  overdue: boolean;
} {
  const d = ymd(iso);
  if (!d) return { label: iso ?? 'ETA unknown', relative: null, overdue: false };
  const diff = daysFromToday(d);
  const relative =
    diff < 0
      ? `${-diff}d overdue`
      : diff === 0
        ? 'today'
        : diff === 1
          ? 'tomorrow'
          : `in ${diff} days`;
  return { label: dateLabel(d), relative, overdue: diff < 0 };
}

// The column header says "Expected" — the S&S board only carries expectedDeliveryDate, not a
// confirmed delivery scan, so a date here must not overstate certainty.
function formatDelivered(iso: string | null): { label: string; ago: string | null } {
  const d = ymd(iso);
  if (!d) return { label: '—', ago: null };
  const past = -daysFromToday(d);
  const ago = past <= 0 ? 'today' : past === 1 ? 'yesterday' : `${past} days ago`;
  return { label: dateLabel(d), ago };
}

function deliveryBadge(shipment: Shipment) {
  if (/transit/i.test(shipment.deliveryStatus ?? '')) {
    return (
      <Badge
        variant="outline"
        className="border-blue-300 text-blue-700 dark:border-blue-800 dark:text-blue-300"
      >
        In transit
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-muted-foreground">
      {shipment.orderStatus || 'Shipped'}
    </Badge>
  );
}

export function InventoryInboundPage() {
  const client = useApiClient();
  const query = useQuery({
    queryKey: INBOUND_KEY,
    // In-transit / delivery status changes over hours/days and the feed hits S&S — don't re-pull on every refocus.
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await client.GET('/inventory/inbound');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load inbound shipments.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  const board = query.data;
  const awaiting = board?.awaitingReceipt ?? [];
  const notTracked = board?.awaitingNotTracked ?? [];
  const dismissed = board?.dismissed ?? [];
  const inTransit = board?.shipments ?? [];
  const nothing =
    board &&
    awaiting.length === 0 &&
    notTracked.length === 0 &&
    inTransit.length === 0 &&
    dismissed.length === 0 &&
    !board.degraded;

  return (
    <div className="space-y-5">
      <div className="rounded-lg border bg-muted/30 px-3.5 py-2.5 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">On the way from S&amp;S</span> — every
        shipment on your S&amp;S account, ordered here or on ssactivewear.com. Nothing here changes
        stock.
      </div>

      {query.isError && !board && (
        <Alert variant="destructive">
          <AlertTitle>Couldn't load inbound shipments</AlertTitle>
          <AlertDescription>
            {query.error instanceof Error ? query.error.message : 'Unknown error. Try refreshing.'}
          </AlertDescription>
        </Alert>
      )}

      {board?.degraded && (
        <Alert>
          <AlertTriangle className="size-4" />
          <AlertTitle>S&amp;S was briefly unreachable</AlertTitle>
          <AlertDescription>
            Some shipments may be missing. Try Refresh in a moment.
          </AlertDescription>
        </Alert>
      )}

      {query.isLoading && <Skeleton className="h-48 w-full" />}

      {nothing && (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <Truck className="size-8 text-muted-foreground" />
            <p className="font-medium">Nothing inbound</p>
            <p className="text-sm text-muted-foreground">
              No S&amp;S shipments on the way and nothing waiting to be scanned in.
            </p>
          </CardContent>
        </Card>
      )}

      {awaiting.length > 0 && (
        <section className="space-y-2.5">
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-3.5 py-2.5 dark:border-amber-900/60 dark:bg-amber-950/30">
            <p className="flex items-center gap-1.5 text-sm font-medium text-amber-900 dark:text-amber-200">
              <PackageCheck className="size-4" aria-hidden />
              Delivered — needs receiving ({awaiting.length})
            </p>
            <p className="mt-0.5 text-xs text-amber-800 dark:text-amber-300/90">
              Arrived but not scanned in — {board?.kpis.awaitingReceiptPieces} pieces not counted in
              stock yet. Scan them in{' '}
              <Link
                to="/inventory/receiving"
                className="font-medium underline underline-offset-2 hover:no-underline"
              >
                Receiving
              </Link>
              .
            </p>
          </div>
          <ShipmentList shipments={awaiting} mode="awaiting" />
        </section>
      )}

      {notTracked.length > 0 && <NotTrackedSection shipments={notTracked} />}

      {inTransit.length > 0 && board && (
        <section className="space-y-2.5">
          <p className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5 font-medium text-foreground">
              <Truck className="size-4" aria-hidden /> In transit
            </span>
            <span>
              · <span className="font-medium text-foreground">{board.kpis.shipmentCount}</span>{' '}
              {board.kpis.shipmentCount === 1 ? 'shipment' : 'shipments'} ·{' '}
              <span className="font-medium text-foreground">{board.kpis.totalPieces}</span> pieces
              on the way ·{' '}
              <span className="font-medium text-foreground">{board.kpis.blankCount}</span>{' '}
              {board.kpis.blankCount === 1 ? 'blank' : 'blanks'}
            </span>
            {board.kpis.shortShipCount > 0 && (
              <span className="font-medium text-amber-700 dark:text-amber-400">
                · {board.kpis.shortShipCount} short-shipped
              </span>
            )}
          </p>
          <ShipmentList shipments={inTransit} mode="transit" />
        </section>
      )}

      {dismissed.length > 0 && <DismissedSection shipments={dismissed} />}
    </div>
  );
}

/**
 * Delivered on the S&S account but carrying nothing FSP stocks — collapsed and neutral by design (no
 * amber, no "needs" verb): the owner orders personal/other goods on the same account, and framing those
 * as receiving work is exactly the F3 noise P1 removes.
 */
function NotTrackedSection({ shipments }: { shipments: Shipment[] }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="space-y-2.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-1.5 rounded-lg border bg-muted/30 px-3.5 py-2.5 text-left text-sm text-muted-foreground hover:bg-muted/50"
      >
        {open ? (
          <ChevronDown className="size-4" aria-hidden />
        ) : (
          <ChevronRight className="size-4" aria-hidden />
        )}
        <span className="font-medium text-foreground">Not tracked by FSP ({shipments.length})</span>
        <span className="text-xs">— nothing in these we stock. Dismiss to clear.</span>
      </button>
      {open && <ShipmentList shipments={shipments} mode="notTracked" />}
    </section>
  );
}

/** Dismissed shipments — the restore section, so a mis-click is never final. */
function DismissedSection({ shipments }: { shipments: Shipment[] }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="space-y-2.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-1.5 rounded-lg border border-dashed px-3.5 py-2 text-left text-xs text-muted-foreground hover:bg-muted/30"
      >
        {open ? (
          <ChevronDown className="size-3.5" aria-hidden />
        ) : (
          <ChevronRight className="size-3.5" aria-hidden />
        )}
        Dismissed ({shipments.length}) — restore any time.
      </button>
      {open && <ShipmentList shipments={shipments} mode="dismissed" />}
    </section>
  );
}

/**
 * The inbound board as CARDS (UI refresh PR-7b). It was a 760px table with a 520px table nested
 * inside each expanded row — two horizontal scrollers on a phone, and the Receive button lived in the
 * last cell of the outer one. A shipment is a small object with a handful of facts, so it reads
 * better as a card at every width than as seven columns.
 */
function ShipmentList({ shipments, mode }: { shipments: Shipment[]; mode: Mode }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <ul className="space-y-2">
      {shipments.map((s) => {
        const key = s.invoiceNumber ?? s.orderNumber;
        return (
          <ShipmentCard
            key={key}
            shipment={s}
            rowKey={key}
            mode={mode}
            isOpen={expanded.has(key)}
            onToggle={() => toggle(key)}
          />
        );
      })}
    </ul>
  );
}

/**
 * When this shipment is due (or was due). ⚠️ In `awaiting` mode the date is S&S's EXPECTED delivery
 * date, not a confirmed delivery — the column used to be headed "Delivered", which read as fact. It
 * says "Expected" now, because that is what the field is.
 */
function whenLine(s: Shipment, mode: Mode): { label: string; note: string | null; warn: boolean } {
  if (mode === 'awaiting') {
    const d = formatDelivered(s.expectedDeliveryDate);
    return { label: d.label === '—' ? 'No date' : `Expected ${d.label}`, note: d.ago, warn: false };
  }
  const eta = formatEta(s.expectedDeliveryDate);
  return { label: eta.label, note: eta.relative, warn: Boolean(eta.overdue) };
}

function ShipmentCard({
  shipment: s,
  rowKey,
  mode,
  isOpen,
  onToggle,
}: {
  shipment: Shipment;
  rowKey: string;
  mode: Mode;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const shortShip = s.lines.some((l) => l.shortShipped);
  const when = whenLine(s, mode);
  const from = [s.shippingCarrier, s.warehouseAbbr ? `${s.warehouseAbbr} warehouse` : null]
    .filter(Boolean)
    .join(' · ');

  return (
    <li className="overflow-hidden rounded-lg border bg-background">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2 p-3">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-label={isOpen ? 'Hide blanks in this shipment' : 'Show blanks in this shipment'}
          className="min-w-0 flex-1 text-left"
        >
          <span className="flex flex-wrap items-center gap-2">
            {isOpen ? (
              <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            ) : (
              <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            )}
            <span className="font-medium">{when.label}</span>
            {when.note && (
              <span
                className={cn(
                  'text-xs',
                  when.warn
                    ? 'font-medium text-amber-700 dark:text-amber-400'
                    : 'text-muted-foreground',
                )}
              >
                {when.note}
              </span>
            )}
          </span>
          <span className="mt-1 block pl-6 text-xs text-muted-foreground">
            <span className="font-mono">#{s.orderNumber}</span>
            {from && ` · ${from}`}
            {` · ${s.totalPieces} ${s.totalPieces === 1 ? 'piece' : 'pieces'}`}
          </span>
        </button>

        <span className="flex shrink-0 flex-wrap items-center gap-1.5">
          {mode === 'awaiting' ? (
            <Badge
              variant="outline"
              className="border-amber-300 text-amber-700 dark:border-amber-800 dark:text-amber-400"
            >
              Needs receiving
            </Badge>
          ) : mode === 'notTracked' ? (
            <Badge variant="outline" className="text-muted-foreground">
              Not tracked
            </Badge>
          ) : mode === 'dismissed' ? (
            <Badge variant="outline" className="text-muted-foreground">
              Dismissed
            </Badge>
          ) : (
            deliveryBadge(s)
          )}
          {s.deliveryState === 'partially_delivered' && mode !== 'transit' && (
            <Badge
              variant="outline"
              className="border-amber-300 text-[10px] font-normal text-amber-700 dark:border-amber-800 dark:text-amber-400"
              title="S&S shows some boxes delivered and some still with the carrier — open Receiving to see which"
            >
              Some boxes still out
            </Badge>
          )}
          {s.boxesReceived != null && mode !== 'transit' && (
            <Badge
              variant="outline"
              className="border-amber-300 text-[10px] font-normal text-amber-700 dark:border-amber-800 dark:text-amber-400"
              title="Received box by box — the rest is still to come"
            >
              {s.boxesReceived} of {s.totalBoxes ?? '?'} boxes received
            </Badge>
          )}
          {mode === 'transit' && !s.forFspStock && (
            <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground">
              not tracked
            </Badge>
          )}
          {shortShip && (
            <Badge
              variant="outline"
              className="border-amber-300 text-[10px] font-normal text-amber-700 dark:border-amber-800 dark:text-amber-400"
            >
              S&amp;S shipped short
            </Badge>
          )}
        </span>

        <span className="flex w-full items-center gap-2 sm:w-auto">
          {s.trackingNumber &&
            (s.trackingUrl ? (
              <a
                href={s.trackingUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm text-primary underline-offset-2 hover:underline"
              >
                Track <ExternalLink className="size-3" aria-hidden />
              </a>
            ) : (
              <span className="font-mono text-xs text-muted-foreground">{s.trackingNumber}</span>
            ))}
          {mode === 'awaiting' && s.invoiceNumber && (
            <Button asChild size="sm" variant="outline" className="flex-1 sm:flex-initial">
              <Link to="/inventory/receiving" search={{ code: s.invoiceNumber }}>
                Receive
              </Link>
            </Button>
          )}
          <DismissCell shipment={s} mode={mode} />
        </span>
      </div>

      {isOpen && (
        <ul className="border-t bg-muted/20 text-sm">
          {s.lines.map((l) => (
            <li
              key={`${rowKey}-${l.ssSku}`}
              className="flex flex-wrap items-center gap-x-3 gap-y-0.5 border-t px-3 py-2 first:border-t-0"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{blankLabel(l)}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {variantLabel(l) && `${variantLabel(l)} · `}
                  <span className="font-mono">{l.ssSku}</span>
                </span>
              </span>
              <span className="text-right text-xs text-muted-foreground">
                {l.qtyOrdered} ordered
              </span>
              <span className="text-right text-xs">
                <span
                  className={cn(
                    'font-medium tabular-nums',
                    l.shortShipped && 'text-amber-700 dark:text-amber-400',
                  )}
                >
                  {l.qtyShipped}
                </span>{' '}
                shipped
                {l.shortShipped && (
                  <span className="text-amber-700 dark:text-amber-400"> (−{l.shortShipQty})</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * Dismiss / Restore for one shipment — writer-gated (the server 403 is the real guard), keyed by S&S
 * invoice (a rare invoice-less row can't be dismissed). No confirm prompt: dismiss is display-only and
 * the Dismissed section is the undo. Success refetches the board, so the hub badge self-corrects.
 */
function DismissCell({ shipment: s, mode }: { shipment: Shipment; mode: Mode }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const { state } = useAuth();
  const canWrite = state.status === 'signed-in' && state.role.isInventoryWrite;
  const invoice = s.invoiceNumber;

  const dismiss = useMutation({
    mutationFn: async (path: '/inventory/inbound/dismiss' | '/inventory/inbound/undismiss') => {
      if (!invoice) throw new Error('This shipment has no S&S invoice number.');
      const { data, error } = await client.POST(path, { body: { invoiceNumber: invoice } });
      if (error) throw new Error(error?.error?.detail ?? 'The change didn’t save.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: INBOUND_KEY }),
  });

  if (!canWrite || !invoice) return null;
  if (mode === 'dismissed') {
    return (
      <Button
        size="sm"
        variant="outline"
        className="h-7"
        disabled={dismiss.isPending}
        onClick={() => dismiss.mutate('/inventory/inbound/undismiss')}
      >
        <Undo2 className="size-3.5" aria-hidden /> Restore
      </Button>
    );
  }
  return (
    <Button
      size="sm"
      variant="ghost"
      className="h-7 text-muted-foreground hover:text-foreground"
      title="Hide this shipment from the board and the badge (reversible in the Dismissed section)."
      disabled={dismiss.isPending}
      onClick={() => dismiss.mutate('/inventory/inbound/dismiss')}
    >
      <X className="size-3.5" aria-hidden /> Dismiss
    </Button>
  );
}
